<template>
    <div v-if="!$auth.loading" id="app" :class="[skinClasses]" class="h-100">
        <component :is="layout">
            <router-view />
        </component>
    </div>
</template>

<script>
import { getInstance } from "@/auth/auth0";
import { getUserData } from "@/auth/getUserData";
import useAws from "@/aws/useAws";
import useActiveChat from "@/composables/useActiveChat";
import useConversationExports from "@/composables/useConversationExports";
import { useCookies } from "@/composables/useCookies";
import useEmailFilters from "@/composables/useEmailFilters";
import useMessagingFilters from "@/composables/useMessagingFilters";
import useWebNotifications from "@/composables/useWebNotifications";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { provideTour } from "@/libs/tour";
import { provideWebsockets, useWebsockets } from "@/libs/websockets";
import router from "@/router";

import store from "@/store";
import { useChatsStore } from "@/store/useChatsStore";
import { decrypt } from "@/utils/crypto";
import { transformResponseMessageToErrorMessage } from "@/utils/exports";

import { isMobile } from "@/utils/mobile";

import "@/assets/scss/toastification.scss";
import useActiveTeamChat from "@/views/modules/team-chat/useActiveTeamChat";
import useTeamChat from "@/views/modules/team-chat/useTeamChat";
import useAppConfig from "@core/app-config/useAppConfig";
import { InboxModulePathType } from "@saysimple-platform/dtos";
// This will be populated in `beforeCreate` hook
import { $themeBreakpoints, $themeColors, $themeConfig } from "@themeConfig";
import { computed, nextTick, watch } from "@vue/composition-api";

import { useCssVar, useIdle, useWindowSize } from "@vueuse/core";
import { v4 as uuidv4 } from "uuid";
import { localize } from "vee-validate";
import { provideToast, useToast } from "vue-toastification/composition";
import { InboxModuleType } from "@/types/inboxModule.type";
import useSoundNotifications from "@/composables/useSoundNotifications";

const LayoutVertical = () => import("@/layouts/vertical/layoutVertical.vue");
const LayoutFull = () => import("@/layouts/full/layoutFull.vue");

export default {
    components: {
        LayoutVertical,
        LayoutFull,
    },

    // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
    // Currently, router.currentRoute is not reactive and doesn't trigger any change
    computed: {
        layout() {
            if (this.$route.meta.layout === "full") {
                return "layout-full";
            }
            return `layout-${this.contentLayoutType}`;
        },
        contentLayoutType() {
            return this.$store.state.appConfig.layout.type;
        },
    },
    beforeCreate() {
        // Set colors in theme
        const colors = ["primary", "secondary", "success", "info", "warning", "danger", "light", "dark"];

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
        }

        // Set Theme Breakpoints
        const breakpoints = ["xs", "sm", "md", "lg", "xl"];

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(
                useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2)
            );
        }

        // Set RTL
        const { isRTL } = $themeConfig.layout;
        document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    },
    setup() {
        // Redirect to the given landing page
        const searchParams = new URLSearchParams(window.location.search);
        const landingPage = searchParams.get("landing_page");

        if (landingPage) {
            return router.push(landingPage);
        }

        const { skin, skinClasses } = useAppConfig();
        const inTeamChat = computed(() => ["team-chat", "team-chat-id"].includes(router?.history?.current?.name));

        // @TEMP placed it here for the time being, because the complete vee-validate should be multilingual.
        localize({
            nl: {
                fields: {
                    phone: {
                        libphonenumber: i18n.t("validation.invalid-phone"),
                    },
                },
            },
            en: {
                fields: {
                    phone: {
                        libphonenumber: i18n.t("validation.invalid-phone"),
                    },
                },
            },
            es: {
                fields: {
                    phone: {
                        libphonenumber: i18n.t("validation.invalid-phone"),
                    },
                },
            },
        });

        // If skin is dark when initialized => Add class to body
        if (skin.value === "dark") {
            document.body.classList.add("dark-layout");
        }
        provideTour();

        // Provide toast for Composition API usage
        // This for those apps/components which uses composition API
        // Demos will still use Options API for ease
        // @NOTICE if you change something here, also change it in @/libs/toastification (thats used for Composition API)
        provideToast({
            hideProgressBar: true,
            closeOnClick: true,
            closeButton: false,
            icon: false,
            timeout: 3000,
            transition: "Vue-Toastification__fade",
        });
        const toast = useToast();

        // Provide websockets
        provideWebsockets();
        const { websockets } = useWebsockets();

        const instance = getInstance();

        instance.$watch("loading", async (isLoading) => {
            if (!isLoading) {
                if (instance.isAuthenticated) {
                    // SIGNED COOKIES ** For AWS S3 bucket policies
                    // https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-signed-cookies.html#private-content-signed-cookie-misuse
                    useAws.getSignedCookies(getUserData().storageName).then((signedCookies) => {
                        const { setSignedCookies } = useCookies();
                        setSignedCookies(signedCookies);
                    });

                    // SOCKET AUTH HEADER
                    const accessToken = (await getInstance().auth0Client.getTokenSilently({ detailedResponse: true }))
                        ?.id_token;
                    websockets.io.opts.query = {
                        accessToken,
                    };

                    websockets.connect();

                    // Fetch the filters from localStorage per inbox module type. Only when the customer settings allows it.
                    useMessagingFilters().fetchFiltersFromLocalStorage();
                    useEmailFilters().fetchFiltersFromLocalStorage();
                }
            }
        });

        const { notifyTeamchatMessage, notifyTeamchatStarted, vueUseWebNotification } = useWebNotifications();
        const {
            soundNotifyTeamchatMessage,
            soundNotifyTeamchatStarted,
            audioInstance,
            soundNotifyConversationUnassigned,
            soundNotifyConversationAssigned,
            soundNotifyIncomingMessageNew,
            soundNotifyIncomingMessage,
        } = useSoundNotifications();

        const { conversationsExportStatus, conversationsExportFileUrl } = useConversationExports();
        const { getActiveChat } = useActiveChat();
        const { activeChat, scrollToBottomInActiveChatLog, loadUsersAndParticipants } = useActiveTeamChat();
        const { chats, updateEntryStatus, fetchChat } = useTeamChat();

        const displayWebNotification = (text, chatId, route = InboxModulePathType.MESSAGING) => {
            const { onClick, show, isSupported } = vueUseWebNotification({
                title: text,
                dir: "auto",
                lang: "en",
                renotify: true,
                tag: "test",
            });

            if (isSupported) {
                show();
            }

            onClick.on(() => {
                router.replace({
                    name: route,
                    params: {
                        chatId,
                    },
                });
            });
        };

        const playSoundNotification = (file = "notification") => {
            return audioInstance(`${process.env.VUE_APP_MFE_STATIC_ENDPOINT}/audio/${file}.mp3`)
                .play()
                .catch((e) => console.error(e)); // No problem to log to console of customers.
        };

        // @see https://justinternet.atlassian.net/wiki/spaces/SAYS/pages/647004161/Frontend+events
        websockets.on("socketEvent", (socketMessage) => {
            const wsMessage = decrypt(socketMessage.event, socketMessage.iv);

            if (wsMessage.type === "userUpdated" && parseInt(wsMessage.payload.id) === getUserData().id) {
                store.commit("userConfig/UPDATE_STATUS", wsMessage.payload.updates.status);
            }

            const excludedEventTypes = [
                "EXPORTS_CONVERSATIONS",
                "teamChatsReceiveMessage",
                "groupChatCreated",
                "teamChatDeleted",
                "teamChatParticipantAdded",
                "teamChatParticipantRemoved",
                "teamChatParticipantLeft",
                "teamChatStartTyping",
                "teamChatStopTyping",
                "teamChatStatusChanged",
            ];

            // Do not process events that is coming from the origin sender.
            if (
                (wsMessage.senderId === websockets.id && !excludedEventTypes.includes(wsMessage.type)) ||
                (wsMessage.type === "EXPORTS_CONVERSATIONS" && wsMessage.senderId !== websockets.id)
            ) {
                return;
            }

            let chat;
            let message;
            let messageEntry;
            let payload;

            const playSoundNewConversation = Number(soundNotifyConversationUnassigned.value);
            const playSoundNewMessageAssignedToMe = Number(soundNotifyIncomingMessage.value);
            const playSoundNewMessageUnassigned = Number(soundNotifyIncomingMessageNew.value);
            const playSoundConversationAssignedToMe = Number(soundNotifyConversationAssigned.value);

            const { addUnreadChat, deleteUnreadChat, updateUnreadChat } = useUnreadChats(
                wsMessage.payload.inboxModuleType
            );
            const chatsStore = useChatsStore();

            switch (wsMessage.type) {
                case "EXPORTS_CONVERSATIONS":
                    conversationsExportStatus.value = {
                        status: wsMessage.payload.status,
                        message: wsMessage.payload.message,
                    };
                    if (wsMessage.payload.status === "failed") {
                        toast({
                            component: ToastificationContent,
                            props: {
                                title: i18n.t("exports.error.title"),
                                text: i18n.t(transformResponseMessageToErrorMessage(wsMessage.payload.message)),
                                icon: "times",
                                variant: "danger",
                            },
                        });
                        break;
                    }
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("exports.success.title"),
                            text: i18n.t("secure-exports.generate.success"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                    conversationsExportFileUrl.value = wsMessage.payload.signedUrl.signed;
                    break;
                case "teamChatsReceiveMessage":
                    message = {
                        chatEntryId: wsMessage.payload.entry.id,
                        chatParticipantId: wsMessage.payload.participantEntry.chatParticipantId,
                        createdAt: wsMessage.payload.participantEntry.createdAt,
                        id: wsMessage.payload.entry.id,
                        message: wsMessage.payload.entry.message,
                        senderUserId: wsMessage.payload.entry.userId,
                        // 'sent' is the first status to see for the receiver
                        status: "sent",
                        type: wsMessage.payload.entry.type,
                        userId: wsMessage.payload.participantEntry.userId,
                        ticks: wsMessage.payload.ticks,
                        attachments: wsMessage.payload.entry?.attachments,
                    };
                    if (getUserData().id !== wsMessage.payload.entry.userId) {
                        chat = chats.value.find((chat) => chat.id === wsMessage.payload.entry.chatId) ?? {
                            id: wsMessage.payload.entry.chatId,
                        };
                        useUnreadChats(InboxModuleType.TEAM).addUnreadChat({
                            ...chat,
                            chatId: chat.id,
                        });
                    }
                    if (
                        (activeChat.value?.id !== wsMessage.payload.entry.chatId || !inTeamChat) &&
                        wsMessage.payload.entry.userId !== getUserData()?.id
                    ) {
                        if (!chats?.value?.find((chat) => chat.id === wsMessage.payload.entry.chatId)) {
                            fetchChat(wsMessage.payload.entry.chatId).then((chat) => {
                                loadUsersAndParticipants(chat).then((participants) => {
                                    chat.participants = participants;
                                    chat.lastMessage = message;
                                    chat.usersTyping = [];
                                    chat.unreadMessages = chat.unreadMessages ? chat.unreadMessages + 1 : 1;
                                    if (!chats?.value?.find((chat) => chat.id === wsMessage.payload.entry.chatId)) {
                                        chats.value.push(chat);
                                    }
                                });
                            });
                        }

                        if (notifyTeamchatMessage.value) {
                            displayWebNotification(
                                i18n.t("team-chats.notification.new-message"),
                                wsMessage.payload.entry.chatId,
                                "team-chat-id"
                            );
                        }
                        if (soundNotifyTeamchatMessage) {
                            playSoundNotification("notification_teamchat");
                        }
                    }

                    /*
          If the incoming entry belongs to the current active chat, add it to the chat log.
          */
                    if (activeChat.value?.id === wsMessage.payload.entry.chatId && inTeamChat) {
                        updateEntryStatus(
                            "read",
                            wsMessage.payload.entry.chatId,
                            wsMessage.payload.participantEntry.chatParticipantId,
                            wsMessage.payload.participantEntry.id
                        );

                        if (getUserData().id === wsMessage.payload.entry.userId) {
                            activeChat.value.entries.map((entry) =>
                                wsMessage.payload.entry.tmpId === entry.tmpId ? message : entry
                            );
                        } else {
                            activeChat.value.entries.push(message);
                        }

                        activeChat.value = {
                            ...activeChat.value,
                            entries: [...activeChat.value.entries],
                        };

                        if (getUserData().id !== wsMessage.payload.entry.userId) {
                            nextTick(() => {
                                scrollToBottomInActiveChatLog();
                            });
                        }
                    } else {
                        updateEntryStatus(
                            "delivered",
                            wsMessage.payload.entry.chatId,
                            wsMessage.payload.participantEntry.chatParticipantId,
                            wsMessage.payload.participantEntry.id
                        );
                    }

                    /*
          Chats list
          */
                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.entry.chatId);
                    if (!chat) {
                        return;
                    }

                    if (
                        (activeChat.value?.id !== wsMessage.payload.entry.chatId || !inTeamChat) &&
                        getUserData().id !== wsMessage.payload.entry.userId
                    ) {
                        chat.unreadMessages++;
                        chat.read = false;
                    }

                    chat.lastMessage = message;
                    break;
                case "groupChatCreated":
                    if (activeChat.value?.id !== wsMessage.payload.id || !inTeamChat) {
                        chat = wsMessage.payload;
                        loadUsersAndParticipants(wsMessage.payload).then((participants) => {
                            message = {
                                createdAt: wsMessage.payload.createdAt,
                                id: uuidv4(),
                                message: JSON.stringify({
                                    eventType: "TeamChat::groupChatCreated",
                                    payload: wsMessage.payload,
                                }),
                                status: "read",
                                type: "event",
                                userId: wsMessage.payload.userId,
                            };

                            chat.participants = participants;
                            chat.usersTyping = [];
                            chat.lastMessage = message;
                            if (!chats.value?.find((chat) => chat.id === wsMessage.payload.id)) {
                                chats.value.push(chat);
                            }

                            if (notifyTeamchatStarted.value && wsMessage.payload.userId !== getUserData().id) {
                                displayWebNotification(
                                    i18n.t("team-chats.notification.new-chat"),
                                    wsMessage.payload.id,
                                    "team-chat-id"
                                );
                            }

                            if (soundNotifyTeamchatStarted.value && wsMessage.payload.userId !== getUserData().id) {
                                playSoundNotification("notification_teamchat");
                            }
                        });
                    }
                    break;
                case "teamChatDeleted":
                    if (activeChat.value?.id === wsMessage.payload.id && inTeamChat) {
                        message = {
                            createdAt: wsMessage.payload.createdAt,
                            id: uuidv4(),
                            message: JSON.stringify({
                                eventType: "TeamChat::chatDeleted",
                                payload: { chatId: wsMessage.payload.id, createdBy: wsMessage.payload.userId },
                            }),
                            status: "read",
                            type: "event",
                            userId: wsMessage.payload.userId,
                        };
                        activeChat.value.entries.push(message);
                        activeChat.value = {
                            ...activeChat.value,
                            entries: [...activeChat.value.entries],
                            isArchived: true,
                        };
                        useUnreadChats(InboxModuleType.TEAM).deleteUnreadChat(wsMessage.payload.id);

                        nextTick(() => {
                            scrollToBottomInActiveChatLog();
                        });
                    }
                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.id);
                    if (chat) {
                        chat.isArchived = true;
                        chat.lastMessage = message;
                        chats.value = [...chats.value];
                    }
                    break;
                case "teamChatParticipantAdded":
                    loadUsersAndParticipants(activeChat.value).then((participants) => {
                        message = {
                            createdAt: wsMessage.payload.createdAt,
                            id: uuidv4(),
                            message: JSON.stringify({
                                eventType: "TeamChat::participantAdded",
                                payload: wsMessage.payload,
                            }),
                            status: "read",
                            type: "event",
                            userId: wsMessage.payload.userId,
                        };

                        if (activeChat.value?.id === wsMessage.payload.chatId && inTeamChat) {
                            const entries = [...activeChat.value.entries, message];

                            activeChat.value = {
                                ...activeChat.value,
                                ...(wsMessage.payload?.userId === getUserData()?.id ? { isArchived: false } : {}),
                                entries,
                                participants,
                            };

                            nextTick(() => {
                                scrollToBottomInActiveChatLog();
                            });
                        }

                        chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);
                        if (chat) {
                            if (wsMessage.payload?.userId === getUserData()?.id) {
                                chat.isArchived = false;
                            }
                            chat.lastMessage = message;
                            chats.value = [...chats.value];
                        }
                    });
                    break;
                case "teamChatParticipantRemoved":
                    if (activeChat.value?.id === wsMessage.payload.chatId && inTeamChat) {
                        activeChat.value?.participants?.forEach((participant) => {
                            if (participant.userId === wsMessage.payload?.userId) {
                                participant.leftAt = wsMessage.payload.createdAt;
                            }
                        });

                        message = {
                            createdAt: wsMessage.payload.createdAt,
                            id: uuidv4(),
                            message: JSON.stringify({
                                eventType: "TeamChat::participantRemoved",
                                payload: wsMessage.payload,
                            }),
                            status: "read",
                            type: "event",
                            userId: wsMessage.payload.userId,
                        };

                        activeChat.value.entries.push(message);
                        activeChat.value = {
                            ...activeChat.value,
                            ...(wsMessage.payload?.userId === getUserData()?.id ? { isArchived: true } : {}),
                        };

                        nextTick(() => {
                            scrollToBottomInActiveChatLog();
                        });
                    }

                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);
                    if (chat) {
                        if (wsMessage.payload?.userId === getUserData()?.id) {
                            chat.isArchived = true;
                        }
                        chat.lastMessage = message;
                        chats.value = [...chats.value];
                    }
                    break;
                case "teamChatParticipantLeft":
                    if (activeChat.value?.id === wsMessage.payload.chatId && inTeamChat) {
                        activeChat.value?.participants?.forEach((participant) => {
                            if (participant.userId === wsMessage.payload?.userId) {
                                participant.leftAt = wsMessage.payload.createdAt;
                            }
                        });

                        message = {
                            createdAt: wsMessage.payload.createdAt,
                            id: uuidv4(),
                            message: JSON.stringify({
                                eventType: "TeamChat::participantLeft",
                                payload: wsMessage.payload,
                            }),
                            status: "read",
                            type: "event",
                            userId: wsMessage.payload.userId,
                        };

                        activeChat.value.entries.push(message);
                        activeChat.value = {
                            ...activeChat.value,
                            ...(wsMessage.payload?.userId === getUserData()?.id ? { isArchived: true } : {}),
                        };

                        nextTick(() => {
                            scrollToBottomInActiveChatLog();
                        });
                    }

                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);
                    if (chat) {
                        // if is the current use who leave the chat we delete the unreadChat
                        if (getUserData().id === wsMessage.payload.userId) {
                            useUnreadChats(InboxModuleType.TEAM).deleteUnreadChat(chat.id);
                        }
                        chat.lastMessage = message;
                        if (wsMessage.payload?.userId === getUserData()?.id) {
                            chat.isArchived = true;
                        }
                        chats.value = [...chats.value];
                    }
                    break;
                case "teamChatStartTyping":
                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);
                    if (!chat) {
                        return;
                    }

                    if (chat.isArchived) {
                        return;
                    }

                    if (chat.usersTyping.some((user) => user.id === wsMessage.payload.userId) === false) {
                        chat.usersTyping.push({
                            id: wsMessage.payload.userId,
                            name: wsMessage.payload.userName,
                        });
                    }

                    if (activeChat.value?.id === wsMessage.payload.chatId && inTeamChat) {
                        activeChat.value.usersTyping = chat.usersTyping;
                    }
                    break;
                case "teamChatStopTyping":
                    chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);

                    if (!chat) {
                        return;
                    }

                    chat.usersTyping = chat.usersTyping?.filter((user) => user.id !== wsMessage.payload.userId);

                    if (activeChat.value?.id === wsMessage.payload.chatId && inTeamChat) {
                        activeChat.value.usersTyping = chat.usersTyping;
                    }
                    break;
                case "teamChatStatusChanged":
                    if (activeChat.value?.id === wsMessage.payload.chatId && activeChat.value.entries && inTeamChat) {
                        activeChat.value.entries?.forEach((entry) => {
                            entry.ticks?.forEach((tick) => {
                                if (
                                    tick.chatParticipantId === wsMessage.payload.invokingParticipantId &&
                                    tick.chatEntryId === wsMessage.payload.chatEntryId
                                ) {
                                    tick.status = wsMessage.payload.entryStatus;
                                }
                            });
                        });

                        activeChat.value = {
                            ...activeChat.value,
                        };
                        chat = chats.value.find((chat) => chat.id === wsMessage.payload.chatId);
                        if (chat) {
                            useUnreadChats(InboxModuleType.TEAM).updateUnreadChat({
                                ...chat,
                                chatId: chat.id,
                            });
                        }
                    }
                    break;
                case "userUpdated":
                    if (inTeamChat) {
                        chats.value?.forEach((chat) => {
                            const participant = chat?.participants?.find(
                                (participant) => participant.userId === parseInt(wsMessage.payload.id)
                            );
                            if (participant) {
                                participant.user.status = wsMessage.payload.updates.status;
                            }
                        });

                        const participant = activeChat.value?.participants?.find(
                            (participant) => participant.userId === parseInt(wsMessage.payload.id)
                        );
                        if (participant) {
                            participant.user.status = wsMessage.payload.updates.status;
                        }
                    }

                    break;
                case "newConversation":
                    // @NOTICE addUnreadChat will only add unread chat when it's not there already, so no duplicates will occur in the counters.
                    addUnreadChat({
                        chatId: wsMessage.payload.conversation.id,
                        channelId: wsMessage.payload.conversation.channelId,
                        tagIds:
                            wsMessage.payload.inboxModuleType === InboxModuleType.EMAIL
                                ? wsMessage.payload.conversationTags
                                : wsMessage.payload.conversation.tags.map((tag) => tag.id),
                        assignedUserId: wsMessage.payload.conversation.assignedUser,
                        assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                        status: wsMessage.payload.conversation.status,
                    });

                    if (
                        wsMessage.payload.conversation.direction === "in" &&
                        Number(soundNotifyConversationUnassigned.value) &&
                        !wsMessage.payload.conversation.assignedUser &&
                        !wsMessage.payload.conversation.assignedTeam
                    ) {
                        playSoundNotification();
                    }

                    break;
                case "receivedMessage":
                    // Only when the incoming chat entry id is equal to the active chat id, we do NOT add unread.
                    if (getActiveChat.value?.chatLog[0].id !== wsMessage.payload.conversation.id) {
                        // @NOTICE addUnreadChat will only add unread chat when it's not there already, so no duplicates will occur in the counters.
                        addUnreadChat({
                            chatId: wsMessage.payload.conversation.id,
                            channelId: wsMessage.payload.conversation.channelId,
                            tagIds:
                                wsMessage.payload.inboxModuleType === InboxModuleType.EMAIL
                                    ? wsMessage.payload.conversationTags
                                    : wsMessage.payload.conversation.tags.map((tag) => tag.id),
                            assignedUserId: wsMessage.payload.conversation.assignedUser,
                            assignedTeamId: wsMessage.payload.conversation.assignedTeam,
                            status: wsMessage.payload.conversation.status,
                        });
                    }

                    if (
                        wsMessage.payload.message.direction === "in" &&
                        // New message in 'new'
                        ((Number(soundNotifyIncomingMessageNew.value) &&
                            !wsMessage.payload.conversation.assignedUser &&
                            !wsMessage.payload.conversation.assignedTeam) ||
                            // New message in conversation assigned to you
                            (Number(soundNotifyIncomingMessage.value) &&
                                wsMessage.payload.conversation.assignedUser === getUserData().id))
                    ) {
                        playSoundNotification();
                    }

                    break;
                case "conversationUpdate":
                    if (wsMessage.payload.payload.status === ChatStatusType.CLOSED) {
                        deleteUnreadChat(wsMessage.payload.payload.id);
                        return;
                    }

                    updateUnreadChat({
                        chatId: wsMessage.payload.payload.id,
                        channelId: wsMessage.payload.payload.channelId,
                        tagIds: wsMessage.payload.payload.tags?.map((tag) => tag.id),
                        assignedUserId: wsMessage.payload.payload.assignedUser,
                        assignedTeamId: wsMessage.payload.payload.assignedTeam,
                        status: wsMessage.payload.payload.status,
                    });

                    if (
                        Number(soundNotifyConversationAssigned.value) &&
                        wsMessage.payload.payload.assignedUser === getUserData().id &&
                        wsMessage.payload.prepayload.assignedUser !== wsMessage.payload.payload.assignedUser
                    ) {
                        playSoundNotification();
                    }

                    break;
                case "NEW_CHAT":
                    payload = wsMessage.payload;

                    useUnreadChats(InboxModuleType.MESSAGING).updateUnreadChat({
                        channelId: payload.chat.channelId,
                        assignedUserId: payload.chat.assignedUserId,
                        assignedTeamId: payload.chat.assignedTeamId,
                        chatId: payload.chat.id,
                        tagIds: payload.chat.tagIds,
                        isChatRead: payload.chat.read,
                        status: payload.chat.status,
                    });

                    // @note sound notification via NEW_ENTRY event.

                    chatsStore.fetchChatsList();
                    break;
                case "NEW_ENTRY":
                    payload = wsMessage.payload;

                    // ONLY if the entry is of specific type, it's actually a MESSAGE from A to B or other way around.
                    messageEntry = payload.entries.find((entry) =>
                        ["text", "attachment", "location"].includes(entry.type)
                    );
                    if (!messageEntry) {
                        return;
                    }

                    if (messageEntry.direction !== "in" || chatsStore.activeChat?.chat.id === payload.chat.id) {
                        return;
                    }

                    updateUnreadChat({
                        chatId: payload.chat.id,
                        assignedTeamId: payload.chat.assignedTeamId,
                        assignedUserId: payload.chat.assignedUserId,
                        channelId: payload.chat.channelId,
                        tagIds: payload.chat.tagIds,
                        isChatRead: payload.chat.read,
                        status: payload.chat.status,
                    });

                    if (
                        !playSoundNewConversation &&
                        !playSoundNewMessageAssignedToMe &&
                        !playSoundNewMessageUnassigned
                    ) {
                        return;
                    }

                    // For readability, if-then-else construction.
                    if (playSoundNewConversation && !payload.chat.assignedUserId && !payload.chat.assignedTeamId) {
                        // "New conversation"
                        playSoundNotification();
                    } else if (playSoundNewMessageAssignedToMe && payload.chat.assignedUserId === getUserData().id) {
                        // "New message in to-me assigned conversation"
                        playSoundNotification();
                    } else if (
                        playSoundNewMessageUnassigned &&
                        !payload.chat.assignedUserId &&
                        !payload.chat.assignedTeamId
                    ) {
                        // "New message in not-assigned conversation"
                        playSoundNotification();
                    }

                    break;
                case "CHAT_UPDATE":
                    payload = wsMessage.payload;

                    updateUnreadChat({
                        chatId: payload.chat.id,
                        assignedTeamId: payload.chat.assignedTeamId,
                        assignedUserId: payload.chat.assignedUserId,
                        channelId: payload.chat.channelId,
                        tagIds: payload.chat.tagIds,
                        status: payload.chat.status,
                    });

                    // @todo like the old situation, the backend should also send its old assignee,
                    //  so we can check if the assignedUserId actually has been changed.
                    // For now really dirty hack to check if the assignmentLastChangedAt is changed within 3 seconds.
                    if (
                        playSoundConversationAssignedToMe &&
                        payload.chat.assignedUserId === getUserData().id &&
                        diff(luxon.DateTime.now().toUTC(), payload.chat.assignmentLastChangedAt).milliseconds < 3000
                    ) {
                        playSoundNotification();
                    }

                    break;
                case "READ_FOR_ALL":
                    payload = wsMessage.payload;

                    // @note that we do not update chats list, because that is computed (so reactive) based on unread chats
                    useUnreadChats(payload.inboxModuleType).deleteUnreadChat(payload.chatId);

                    break;
                case "UNREAD_FOR_ALL":
                    payload = wsMessage.payload;

                    // @note that we do not update chats list, because that is computed (so reactive) based on unread chats
                    useUnreadChats(payload.inboxModuleType).updateUnreadChat({
                        // We need al those props, so the unread counters are shown in correct status filters
                        chatId: payload.chatId,
                        channelId: payload.channelId,
                        userId: payload.userId,
                        tagIds: payload.tagIds,
                        assignedUserId: payload.assignedUserId,
                        assignedTeamId: payload.assignedTeamId,
                        readForAll: payload.readForAll,
                        isChatRead: false,
                        lastEntryReadAt: payload.lastEntryReadAt,
                        status: payload.status,
                    });

                    break;
            }
            // @todo Maybe CHAT_UPDATE event?
        });

        // Don't logout on pwa customers
        if (!(isMobile() && localStorage.getItem("mobileDomainOverride"))) {
            const { idle } = useIdle(8 * 60 * 60 * 1000); // 8 hours
            watch(idle, () => {
                const instance = getInstance();
                if (idle.value && instance.isAuthenticated) {
                    instance.auth0Client.logout({ returnTo: window.location.origin });
                }
            });
        }

        // Set Window Width in store
        store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
        const { width: windowWidth } = useWindowSize();
        watch(windowWidth, (val) => {
            store.commit("app/UPDATE_WINDOW_WIDTH", val);
        });
        return {
            skinClasses,
        };
    },
};
</script>
